import * as ActionTypes from './actionTypes'
import {createActionObject} from '../../../redux/utils'
import {rpcCall} from '../../../util/rpc/rpcCalls'
import {
    getCanManageProjectIds,
    getCustomerIdsOrder,
    getCustomersSearchText,
    getEditingLevel,
    getEditingUserId,
    getProjectIdsOrder,
    getProjectsSearchText,
    getSearchText,
    getTaskIdsOrder,
    getTasksSearchText,
    needMoreCustomers,
    needMoreProjects,
    needMoreTasks
} from './selectors'
import * as EntitiesActions from '../../../entities/actions'
import {addUser} from '../../../entities/actions'
import {HierarchyLevels, UserHierarchyAssignmentEntityType} from "constants"
import {clearTabsLoaded as clearPanelTabsLoaded} from "../workAssignmentPanel/actions"
import actionsGenerator from '../hierarchyAssignmentsEditor/actionsGenerator'
import {setWorkAssignmentsCountersForUser} from '../workAssignmentsTab/actions'
import {handleConcurrentlyRemovedEntitiesAction, hasConcurrentlyRemovedEntities} from '../hierarchyAssignmentsEditor/utils'
import {handleCommonError, handleKeptMaAfterRemoveWaByToast} from '../common/actions'
import {AdditionalDataConstants} from '../constants'
import messageResource from '../../../messageResource'
import {rpcErrorHandler} from './utils'
import {Exceptions} from '../../../panels/user/editTeamAssignmentsPanel/constants'
import {Exceptions as CommonExceptions} from '../../../common/constants'

const hierarchyAssignmentsEditorActions = actionsGenerator({
    actionTypes: ActionTypes
})

const LOAD_LIMIT = 20

export const openEditor = hierarchyAssignmentsEditorActions.openEditor

export const closeEditor = hierarchyAssignmentsEditorActions.closeEditor

export const selectCustomer = hierarchyAssignmentsEditorActions.selectCustomer

export const selectProject = hierarchyAssignmentsEditorActions.selectProject

export const deselectCustomer = hierarchyAssignmentsEditorActions.deselectCustomer

export const deselectProject = hierarchyAssignmentsEditorActions.deselectProject

export const loadMoreEntities = () => dispatch => {
    dispatch(loadMoreCustomers())
    dispatch(loadMoreProjects())
    dispatch(loadMoreTasks())
}

export const loadMoreCustomers = () => (dispatch, getState) => {
    const state = getState()
    if (needMoreCustomers(state)) {
        const userId = getEditingUserId(state)
        const searchText = getCustomersSearchText(state)
        const offset = getCustomerIdsOrder(state).length

        dispatch(createActionObject(ActionTypes.LOAD_CUSTOMER_ASSIGNMENTS_EDITOR_DATA))
        rpcCall('UserManagementService.getCustomerWorkAssignmentsEditorData', [userId, searchText, offset, LOAD_LIMIT], rpcErrorHandler)
            .then(data => {
                dispatch(EntitiesActions.addCustomers(data['customers'].map(c => c['customer'])))
                dispatch(hierarchyAssignmentsEditorActions.loadCustomersSuccess(data, LOAD_LIMIT))
            })
            .catch(error => {
                dispatch(closeEditor())
                dispatch(hierarchyAssignmentsEditorActions.loadCustomersFailure(error))
                dispatch(commonWAEditorPanelErrorHandler(error))
            })
    }
}

export const loadMoreProjects = () => (dispatch, getState) => {
    const state = getState()
    if (needMoreProjects(state)) {
        const userId = getEditingUserId(state)
        const customerId = getEditingLevel(state).customerId
        const searchText = getProjectsSearchText(state)
        const offset = getProjectIdsOrder(state).length

        dispatch(createActionObject(ActionTypes.LOAD_PROJECT_ASSIGNMENTS_EDITOR_DATA))
        rpcCall('UserManagementService.getProjectWorkAssignmentsEditorData', [userId, customerId, searchText, offset, LOAD_LIMIT], rpcErrorHandler)
            .then(data => {
                dispatch(EntitiesActions.addProjects(data['projects'].map(p => p['project'])))
                dispatch(hierarchyAssignmentsEditorActions.loadProjectsSuccess(data, LOAD_LIMIT))
            })
            .catch(error => {
                dispatch(closeEditor())
                dispatch(commonWAEditorPanelErrorHandler(error))
                dispatch(hierarchyAssignmentsEditorActions.loadProjectsFailure(error))
            })
    }
}

export const loadMoreTasks = () => (dispatch, getState) => {
    const state = getState()
    if (needMoreTasks(state)) {
        const userId = getEditingUserId(state)
        const projectId = getEditingLevel(state).projectId
        const searchText = getTasksSearchText(state)
        const offset = getTaskIdsOrder(state).length

        dispatch(createActionObject(ActionTypes.LOAD_TASK_ASSIGNMENTS_EDITOR_DATA))
        rpcCall('UserManagementService.getTaskWorkAssignmentsEditorData', [userId, projectId, searchText, offset, LOAD_LIMIT], rpcErrorHandler)
            .then(data => {
                dispatch(EntitiesActions.addTasks(data['tasks'].map(t => t['task'])))
                dispatch(hierarchyAssignmentsEditorActions.loadTasksSuccess(data, LOAD_LIMIT))
            })
            .catch(error => {
                dispatch(closeEditor())
                dispatch(commonWAEditorPanelErrorHandler(error))
                hierarchyAssignmentsEditorActions.loadTasksFailure(error)
            })
    }
}

const updateWorkAssignmentsTabCounters = userManagementResult => dispatch => {
    const userId = userManagementResult['userInfo']['id']
    const cptCounters = userManagementResult['additionalResultData']['map']['workAssignmentsCounters']
    if (cptCounters) {
        dispatch(setWorkAssignmentsCountersForUser(userId, cptCounters))
    }
    dispatch(addUser(userManagementResult['userInfo']))
}

export const changeCustomerAssignment = (customerId, assigned) => (dispatch, getState) => {
    const state = getState()
    const userId = getEditingUserId(state)

    dispatch(createActionObject(ActionTypes.CHANGE_CUSTOMER_ASSIGNMENT, {
        customerId,
        assigned
    }))

    const method = assigned ? 'UserManagementService.changeUserWorkAssignments' : 'UserManagementService.removeUserWorkAssignments'
    const params = assigned ? [userId, false, [customerId], [], [], [], null, null] : [userId, customerId, UserHierarchyAssignmentEntityType.CUSTOMER, null, null]

    rpcCall(method, params, rpcErrorHandler)
        .then((data) => {
            if (hasConcurrentlyRemovedEntities(data)) {
                dispatch(handleConcurrentlyRemovedEntities(data))
            } else {
                if (!assigned) {
                    const keptCpt = data.additionalResultData.map.keptCptMaAfterRemoveWa
                    if (keptCpt.customersKept || keptCpt.projectsKept || keptCpt.tasksKept || keptCpt.noDeletions) {
                        dispatch(handleKeptMaAfterRemoveWaByToast({cptWaChangesFlags: keptCpt, level: HierarchyLevels.CUSTOMER, all: false}))
                        dispatch(hierarchyAssignmentsEditorActions.setProjectsLoaded(false))
                    } else {
                        dispatch(hierarchyAssignmentsEditorActions.clearCustomersTasksCount([customerId]))
                        dispatch(hierarchyAssignmentsEditorActions.clearCustomersProjectsCount([customerId]))
                    }
                }
                const {someAssigned, allAssigned} = data.additionalResultData.map[AdditionalDataConstants.ALL_ASSIGNED_INFO]
                dispatch(hierarchyAssignmentsEditorActions.changeCustomerAssignmentSuccess(customerId, assigned, undefined, someAssigned, allAssigned))
            }
            dispatch(clearPanelTabsLoaded())
            dispatch(updateWorkAssignmentsTabCounters(data))
        })
        .catch(error => {
            dispatch(commonWAEditorPanelErrorHandler(error))
            dispatch(hierarchyAssignmentsEditorActions.changeCustomerAssignmentFailure(customerId, assigned, error))
        })
}

export const changeProjectAssignment = (projectId, assigned) => (dispatch, getState) => {
    const state = getState()
    const userId = getEditingUserId(state)
    const customerId = getEditingLevel(state).customerId
    const canManage = getCanManageProjectIds(state)[projectId] === true

    dispatch(createActionObject(ActionTypes.CHANGE_PROJECT_ASSIGNMENT, {
        projectId,
        assigned
    }))

    const method = assigned ? 'UserManagementService.changeUserWorkAssignments' : 'UserManagementService.removeUserWorkAssignments'
    const params = assigned ? [userId, false, [], [projectId], [], [], customerId, null] : [userId, projectId, UserHierarchyAssignmentEntityType.PROJECT, customerId, null]

    rpcCall(method, params, rpcErrorHandler)
        .then((data) => {
            const keptCpt = data.additionalResultData.map.keptCptMaAfterRemoveWa
            if (hasConcurrentlyRemovedEntities(data)) {
                dispatch(handleConcurrentlyRemovedEntities(data))
            } else {
                if (!assigned && (keptCpt.customersKept || keptCpt.projectsKept || keptCpt.tasksKept || keptCpt.noDeletions)) {
                    dispatch(handleKeptMaAfterRemoveWaByToast({cptWaChangesFlags: keptCpt, level: HierarchyLevels.PROJECT, all: true}))
                    dispatch(hierarchyAssignmentsEditorActions.setProjectsLoaded(false))
                } else {
                    if (!assigned) dispatch(hierarchyAssignmentsEditorActions.clearProjectsTasksCount([projectId]))
                }
                const {someAssigned, allAssigned} = data.additionalResultData.map[AdditionalDataConstants.ALL_ASSIGNED_INFO]
                dispatch(hierarchyAssignmentsEditorActions.changeProjectAssignmentSuccess(projectId, assigned, canManage, someAssigned, allAssigned))
            }
            dispatch(clearPanelTabsLoaded())
            dispatch(updateWorkAssignmentsTabCounters(data))
        })
        .catch(error => {
            dispatch(hierarchyAssignmentsEditorActions.changeProjectAssignmentFailure(projectId, assigned, error))
            dispatch(commonWAEditorPanelErrorHandler(error))
        })
}

export const changeTaskAssignment = (taskId, assigned) => (dispatch, getState) => {
    const state = getState()
    const userId = getEditingUserId(state)
    const {customerId, projectId} = getEditingLevel(state)

    dispatch(createActionObject(ActionTypes.CHANGE_TASK_ASSIGNMENT, {
        taskId,
        assigned
    }))

    const method = assigned ? 'UserManagementService.changeUserWorkAssignments' : 'UserManagementService.removeUserWorkAssignments'
    const params = assigned ? [userId, false, [], [], [taskId], [], customerId, projectId] : [userId, taskId, UserHierarchyAssignmentEntityType.TASK, customerId, projectId]

    rpcCall(method, params, rpcErrorHandler)
        .then((data) => {
            if (hasConcurrentlyRemovedEntities(data)) {
                dispatch(handleConcurrentlyRemovedEntities(data))
            } else {
                const {someAssigned, allAssigned} = data.additionalResultData.map[AdditionalDataConstants.ALL_ASSIGNED_INFO]
                dispatch(hierarchyAssignmentsEditorActions.changeTaskAssignmentSuccess(taskId, assigned, undefined, someAssigned, allAssigned))
            }
            dispatch(clearPanelTabsLoaded())
            dispatch(updateWorkAssignmentsTabCounters(data))
            if (!assigned) {
                dispatch(handleKeptMaAfterRemoveWaByToast({
                    cptWaChangesFlags: data.additionalResultData.map.keptCptMaAfterRemoveWa,
                    level: HierarchyLevels.TASK,
                    all: false
                }))
            }
        })
        .catch(error => {
            dispatch(hierarchyAssignmentsEditorActions.changeTaskAssignmentFailure(taskId, assigned, error))
            dispatch(commonWAEditorPanelErrorHandler(error))
        })
}

export const changeAllCustomersAssignments = (assigned) => (dispatch, getState) => {
    const state = getState()
    const userId = getEditingUserId(state)

    dispatch(createActionObject(ActionTypes.CHANGE_ALL_ASSIGNMENTS))

    rpcCall('UserManagementService.changeAllCustomersWorkAssignments', [userId, assigned], rpcErrorHandler)
        .then((data) => {
            if (hasConcurrentlyRemovedEntities(data)) {
                dispatch(handleConcurrentlyRemovedEntities(data, true))
            }
            dispatch(hierarchyAssignmentsEditorActions.assignAllCustomersSuccess())
            dispatch(clearPanelTabsLoaded())
            dispatch(updateWorkAssignmentsTabCounters(data))
        })
        .catch(error => {
            dispatch(createActionObject(ActionTypes.CHANGE_ALL_ASSIGNMENTS_FAILURE))
            dispatch(commonWAEditorPanelErrorHandler(error))
        })
}

export const changeAllProjectsAssignments = (assigned) => (dispatch, getState) => {
    const state = getState()
    const userId = getEditingUserId(state)
    const customerId = getEditingLevel(state).customerId

    dispatch(createActionObject(ActionTypes.CHANGE_ALL_ASSIGNMENTS))

    rpcCall('UserManagementService.changeAllProjectsWorkAssignments', [userId, customerId, assigned], rpcErrorHandler)
        .then((data) => {
            if (hasConcurrentlyRemovedEntities(data)) {
                dispatch(handleConcurrentlyRemovedEntities(data, true))
            }
            dispatch(hierarchyAssignmentsEditorActions.assignAllProjectsSuccess())
            dispatch(clearPanelTabsLoaded())
            dispatch(updateWorkAssignmentsTabCounters(data))
        })
        .catch(error => {
            dispatch(createActionObject(ActionTypes.CHANGE_ALL_ASSIGNMENTS_FAILURE))
            dispatch(commonWAEditorPanelErrorHandler(error))
        })
}

export const changeAllTasksAssignments = (assigned) => (dispatch, getState) => {
    const state = getState()
    const userId = getEditingUserId(state)
    const {customerId, projectId} = getEditingLevel(state)

    dispatch(createActionObject(ActionTypes.CHANGE_ALL_ASSIGNMENTS))

    rpcCall('UserManagementService.changeAllTasksWorkAssignments', [userId, customerId, projectId, assigned], rpcErrorHandler)
        .then((data) => {
            if (hasConcurrentlyRemovedEntities(data)) {
                dispatch(handleConcurrentlyRemovedEntities(data, true))
            }
            dispatch(hierarchyAssignmentsEditorActions.assignAllTasksSuccess())
            dispatch(clearPanelTabsLoaded())
            dispatch(updateWorkAssignmentsTabCounters(data))
        })
        .catch(error => {
            dispatch(createActionObject(ActionTypes.CHANGE_ALL_ASSIGNMENTS_FAILURE))
            dispatch(commonWAEditorPanelErrorHandler(error))
        })
}

const handleConcurrentlyRemovedEntities = (data, assignAll) => (dispatch) => {
    dispatch(handleConcurrentlyRemovedEntitiesAction({
        getEditingLevel,
        hierarchyAssignmentsEditorActions,
    })(data, assignAll))
}

export const changeCustomersSearchText = hierarchyAssignmentsEditorActions.changeCustomersSearchText
export const clearCustomersSearchText = hierarchyAssignmentsEditorActions.clearCustomersSearchText

export const changeProjectsSearchText = hierarchyAssignmentsEditorActions.changeProjectsSearchText
export const clearProjectsSearchText = hierarchyAssignmentsEditorActions.clearProjectsSearchText

export const changeTasksSearchText = hierarchyAssignmentsEditorActions.changeTasksSearchText
export const clearTasksSearchText = hierarchyAssignmentsEditorActions.clearTasksSearchText


const commonWAEditorPanelErrorHandler = (error) => (dispatch) => {
    dispatch(handleCommonError(error))

    switch (error.getException()) {
        case Exceptions.NoSuchUserException:
            alert(messageResource.getMessage('work_assignments.panel.copy.error.user_deleted'))
            break
        case CommonExceptions.WORK_ASSIGNMENTS_FEATURE_DISABLED_EXCEPTION:
            alert(messageResource.getMessage("common.turned_off_functionality", [messageResource.getMessage("features.WAS.title")]))
            window.location.reload()
            break
        case Exceptions.UserHasAccessToAllScopeException:
            alert(messageResource.getMessage('work_assignments.error.selected_user_have_entire_scope'))
            window.location.reload()
            break
        case CommonExceptions.NO_RIGHTS_TO_SUBMIT_TT_EXCEPTION:
            alert(messageResource.getMessage('work_assignments.error.selected_user_does_not_have_submit_tt'))
            window.location.reload()
            break
    }
}


// WEBPACK FOOTER //
// ./web/js/administration/userManagement/workAssignmentsEditor/actions.js